import { useState } from 'react';
import {
  Edit,
  Trash2,
  Play,
  AlertCircle,
  MoreVertical,
  Copy,
  RefreshCw,
  Power,
  PowerOff,
  Code,
  Plus,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ConnectorIcon } from '@/components/connectors/ConnectorIcon';
import { Connection, ConnectionStatus, ConnectionType, ConnectorListItem } from '@optidev/shared-types';
import { cn } from '@/lib/utils';

interface ProjectSummary {
  id: string;
  name: string;
}

interface ConnectionsListProps {
  connections: Connection[];
  connectors?: ConnectorListItem[];
  projects?: ProjectSummary[];
  onEdit: (connection: Connection) => void;
  onDelete: (id: string) => void;
  onTest: (id: string) => void;
  onRefreshToken?: (id: string) => void;
  onToggleEnabled?: (id: string, enabled: boolean) => void;
  onViewUsage?: (connection: Connection) => void;
  onAdd?: () => void;
}

// Status badge with colored background
const StatusBadge = ({ status, isEnabled }: { status: ConnectionStatus; isEnabled: boolean }) => {
  if (!isEnabled) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium bg-gray-500/10 text-gray-500">
        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
        Disabled
      </span>
    );
  }

  const config = {
    [ConnectionStatus.ACTIVE]: {
      dotColor: 'bg-emerald-500',
      badgeClass: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      label: 'Active',
    },
    [ConnectionStatus.ERROR]: {
      dotColor: 'bg-red-500',
      badgeClass: 'bg-red-500/10 text-red-600 dark:text-red-400',
      label: 'Error',
    },
    [ConnectionStatus.PENDING]: {
      dotColor: 'bg-amber-500',
      badgeClass: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
      label: 'Pending',
    },
  };

  const { dotColor, badgeClass, label } = config[status] || {
    dotColor: 'bg-gray-400',
    badgeClass: 'bg-gray-500/10 text-gray-500',
    label: 'Unknown',
  };

  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium", badgeClass)}>
      <span className={cn("h-1.5 w-1.5 rounded-full", dotColor)} />
      {label}
    </span>
  );
};

// Format connection type for display
const formatConnectionType = (type: string | undefined): string => {
  if (!type) return 'Unknown';
  return type.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Parse connection name to extract display name and account email
// e.g., "Google Sheets (email@example.com) #2" -> { displayName: "Google Sheets #2", account: "email@example.com" }
const parseConnectionName = (name: string): { displayName: string; account: string | null } => {
  // Match email in parentheses: (something@something.com)
  const emailMatch = name.match(/\(([^)]+@[^)]+)\)/);

  if (emailMatch) {
    const account = emailMatch[1];
    // Remove the (email) part and clean up extra spaces
    const displayName = name.replace(emailMatch[0], '').replace(/\s+/g, ' ').trim();
    return { displayName, account };
  }

  return { displayName: name, account: null };
};

export default function ConnectionsList({
  connections,
  connectors = [],
  projects = [],
  onEdit,
  onDelete,
  onTest,
  onRefreshToken,
  onToggleEnabled,
  onViewUsage,
  onAdd,
}: ConnectionsListProps) {
  // Create a map of connector ID -> connector info for quick lookup
  const connectorsMap = new Map(connectors.map(c => [c.id, c]));

  // Build a map of connectionId -> projects that use it
  const connectionProjectsMap = new Map<string, ProjectSummary[]>();
  for (const project of projects) {
    const connIds = (project as any).connectionIds as string[] | undefined;
    if (connIds) {
      for (const connId of connIds) {
        const existing = connectionProjectsMap.get(connId) || [];
        existing.push({ id: project.id, name: project.name });
        connectionProjectsMap.set(connId, existing);
      }
    }
  }
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleTest = async (id: string) => {
    setTestingId(id);
    try {
      await onTest(id);
    } finally {
      setTestingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    setDeletingId(deleteConfirmId);
    try {
      await onDelete(deleteConfirmId);
    } finally {
      setDeletingId(null);
      setDeleteConfirmId(null);
    }
  };

  const handleRefreshToken = async (id: string) => {
    if (!onRefreshToken) return;
    setRefreshingId(id);
    try {
      await onRefreshToken(id);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleToggleEnabled = async (id: string, currentEnabled: boolean) => {
    if (!onToggleEnabled) return;
    setTogglingId(id);
    try {
      await onToggleEnabled(id, !currentEnabled);
      toast.success(currentEnabled ? 'Connection disabled' : 'Connection enabled');
    } catch {
      toast.error('Failed to update connection');
    } finally {
      setTogglingId(null);
    }
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    toast.success('Connection ID copied to clipboard');
  };

  // Get connection name for delete confirmation
  const connectionToDelete = deleteConfirmId
    ? connections.find(c => c.id === deleteConfirmId)
    : null;

  // Only user OAuth connections (not service accounts/machine credentials)
  const machineAuthMethods = ['service_account', 'service_principal', 'client_credentials'];
  const isUserOAuthConnection = (connection: Connection): boolean => {
    return (connection.authType === ConnectionType.OAUTH ||
           (connection.authMethod?.startsWith('oauth') ?? false)) &&
           !machineAuthMethods.includes(connection.authMethod || '');
  };

  // OAuth or machine credentials (for refresh token functionality)
  const isOAuthConnection = (connection: Connection): boolean => {
    return connection.authType === ConnectionType.OAUTH ||
           connection.authType === ConnectionType.OAUTH_MACHINE ||
           (connection.authMethod?.startsWith('oauth') ?? false);
  };

  if (connections.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground">Your Connections</h3>
        {onAdd && (
          <Button size="sm" variant="outline" onClick={onAdd} className="h-8 text-xs">
            Add Connection
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {connections.map((connection) => {
          const isOAuth = isOAuthConnection(connection);
          const showOAuthBadge = isUserOAuthConnection(connection);
          const isLoading = testingId === connection.id || deletingId === connection.id || refreshingId === connection.id || togglingId === connection.id;
          const isEnabled = connection.enabled !== false;
          const connectorInfo = connectorsMap.get(connection.type);
          const { displayName, account } = parseConnectionName(connection.name);
          const usedByProjects = connectionProjectsMap.get(connection.id) || [];

          return (
            <Card
              key={connection.id}
              className={cn(
                "group transition-colors",
                "hover:border-primary/30",
                !isEnabled && "opacity-60"
              )}
            >
              <CardContent className="p-4 flex flex-col h-full">
                {/* Top: Icon + Name + Status + Menu */}
                <div className="flex items-center gap-3 mb-3">
                  <ConnectorIcon
                    connector={connectorInfo || {
                      icon: 'Database',
                      name: connection.type || 'Unknown',
                    }}
                    className="h-6 w-6 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm text-foreground truncate block">
                      {displayName}
                    </span>
                    {account && (
                      <span className="text-xs text-muted-foreground">{account}</span>
                    )}
                  </div>
                  <StatusBadge status={connection.status} isEnabled={isEnabled} />

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" disabled={isLoading} className="h-7 w-7 p-0 flex-shrink-0">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem
                        onClick={() => handleTest(connection.id)}
                        disabled={isLoading || !isEnabled}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        Test Connection
                      </DropdownMenuItem>

                      <DropdownMenuItem onClick={() => handleCopyId(connection.id)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Connection ID
                      </DropdownMenuItem>

                      {onViewUsage && (
                        <DropdownMenuItem onClick={() => onViewUsage(connection)}>
                          <Code className="h-4 w-4 mr-2" />
                          How to Use
                        </DropdownMenuItem>
                      )}

                      <DropdownMenuSeparator />

                      <DropdownMenuItem onClick={() => onEdit(connection)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Connection
                      </DropdownMenuItem>

                      {isOAuth && onRefreshToken && (
                        <DropdownMenuItem
                          onClick={() => handleRefreshToken(connection.id)}
                          disabled={refreshingId === connection.id || !isEnabled}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          {refreshingId === connection.id ? 'Refreshing...' : 'Refresh Token'}
                        </DropdownMenuItem>
                      )}

                      {onToggleEnabled && (
                        <DropdownMenuItem
                          onClick={() => handleToggleEnabled(connection.id, isEnabled)}
                          disabled={togglingId === connection.id}
                        >
                          {isEnabled ? (
                            <>
                              <PowerOff className="h-4 w-4 mr-2" />
                              {togglingId === connection.id ? 'Disabling...' : 'Disable'}
                            </>
                          ) : (
                            <>
                              <Power className="h-4 w-4 mr-2" />
                              {togglingId === connection.id ? 'Enabling...' : 'Enable'}
                            </>
                          )}
                        </DropdownMenuItem>
                      )}

                      <DropdownMenuSeparator />

                      <DropdownMenuItem
                        onClick={() => setDeleteConfirmId(connection.id)}
                        className="text-destructive focus:text-destructive"
                        disabled={deletingId === connection.id}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* Error message */}
                {connection.errorMessage && (
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-destructive">
                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{connection.errorMessage}</span>
                  </div>
                )}

                {/* Bottom: Meta info */}
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-auto pt-3 border-t border-border/50">
                  <span>{formatConnectionType(connection.type)}</span>
                  {showOAuthBadge && (
                    <>
                      <span className="text-muted-foreground/30">·</span>
                      <span>OAuth</span>
                    </>
                  )}
                  <span className="text-muted-foreground/30">·</span>
                  <span>{new Date(connection.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide pt-2 min-h-[24px]">
                  {usedByProjects.map((project) => (
                    <span
                      key={project.id}
                      className="inline-flex items-center px-2.5 py-1 rounded-[5px] bg-muted text-muted-foreground text-[10px] font-medium leading-none whitespace-nowrap flex-shrink-0"
                    >
                      {project.name}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Connection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>{connectionToDelete?.name}</strong>? This action cannot be
              undone. Any projects using this connection will lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingId ? 'Deleting...' : 'Delete Connection'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}